import {
  UserPlus,
  CreditCard,
  Clock,
  BookOpen,
  ClipboardList,
  Award,
  Calendar,
  Users,
  DollarSign,
  Briefcase,
  BarChart2,
  FileText,
  MessageSquare,
  FolderOpen,
  Mail,
  Heart,
  Globe,
  Library,
  Shield,
  Bell,
  TrendingUp,
} from "lucide-react";

const modules = [
  // 1 — Admissions
  {
    slug: "admissions",
    section: "modules",
    title: "Admissions",
    subtitle: "End-to-end application processing, from inquiry to enrollment",
    icon: UserPlus,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Admissions module manages the complete student lifecycle from initial inquiry through final enrollment. It centralises application intake, document collection, merit ranking, and offer letter issuance in one place.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Educational institutions receive hundreds of applications each cycle. The Admissions module gives admissions teams a structured pipeline to track each applicant's status, collect required documents, run merit-based shortlisting, and communicate decisions — all without spreadsheets or manual emails. Admins configure intake cycles and programme quotas; faculty reviewers score applications within their departments.",
      },
      {
        type: "mockup",
        id: "admissions-form",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Process an Application",
      },
      {
        type: "steps",
        items: [
          {
            title: "Open the Admissions module",
            body: "From the sidebar, navigate to Admissions > Applications. All submitted applications are listed with status indicators: Pending, Under Review, Shortlisted, Offered, or Rejected.",
          },
          {
            title: "Review applicant details",
            body: "Click any application row to open the full profile. You can see personal information, academic history, uploaded documents (mark sheets, ID proof, certificates), and any entrance test scores.",
          },
          {
            title: "Score and shortlist",
            body: "Use the Merit Score panel on the right to enter evaluation marks. When the score meets the programme cutoff, click 'Shortlist'. The system automatically updates the applicant's status and moves them to the shortlisted pool.",
          },
          {
            title: "Issue an offer letter",
            body: "For shortlisted applicants, click 'Generate Offer Letter'. Select the programme, academic year, and fee structure. The system creates a pre-filled PDF letter which you can preview and send via email in one click.",
          },
          {
            title: "Confirm enrollment",
            body: "Once the applicant pays the enrollment fee and submits original documents, change the status to 'Enrolled'. A student account is automatically created and the student appears in the relevant batch and programme roster.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Configurable application forms with custom fields per programme",
          "Document checklist with upload tracking and verification flags",
          "Automated merit ranking based on weighted scoring criteria",
          "Batch import of applications from external admission portals via CSV",
          "Offer letter generation with institution branding and digital signature support",
          "Capacity management with programme-wise seat counters and waitlist handling",
          "Integrated email notifications at each stage transition",
          "Conversion funnel reports showing drop-off rates across the pipeline",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Bulk Update Status' button to shortlist or reject multiple applicants at once after ranking — this saves significant time during peak admission season when hundreds of applications arrive simultaneously.",
      },
    ],
  },

  // 2 — Fee Collection
  {
    slug: "fees",
    section: "modules",
    title: "Fee Collection",
    subtitle: "Manage fee structures, invoices, payments, and arrears in one place",
    icon: CreditCard,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Fee Collection module handles the entire financial transaction lifecycle between the institution and its students — from defining fee structures to collecting payments, issuing receipts, and managing outstanding dues.",
      },
      {
        type: "role-badge",
        roles: ["admin", "student", "parent"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Managing fees across hundreds or thousands of students requires precision. The Fee Collection module lets administrators create fee heads (tuition, hostel, lab, etc.), map them to programmes and batches, and generate invoices automatically. Students and parents can view pending dues and pay online; the finance team tracks collections in real time and chases arrears without manual follow-up.",
      },
      {
        type: "mockup",
        id: "fees-dashboard",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Collect Fees",
      },
      {
        type: "steps",
        items: [
          {
            title: "Configure fee structure",
            body: "Go to Fee Collection > Fee Structure. Create fee heads such as Tuition Fee, Examination Fee, Library Fee, or Sports Fee. Assign each head to the appropriate academic year, programme, and semester. You can set due dates and late fee percentages per head.",
          },
          {
            title: "Generate invoices",
            body: "Navigate to Fee Collection > Generate Invoices. Select the batch and semester, then click 'Generate'. The system creates invoices for all students in the selected group and sends them a notification with the amount due and payment deadline.",
          },
          {
            title: "Accept payment",
            body: "Open any student's fee account from the Pending Payments list. Enter the payment amount, select the mode (Cash, Cheque, Online, DD), and record any concessions or scholarships. The system applies the payment to the oldest outstanding invoice first.",
          },
          {
            title: "Issue receipt",
            body: "After saving the payment, click 'Print Receipt'. The system generates a numbered, timestamped receipt PDF. Receipts are also available in the student's portal for self-service download.",
          },
          {
            title: "Track arrears",
            body: "Go to Fee Collection > Arrears Report. Filter by programme, batch, or due date to see all overdue students. Use 'Send Reminder' to dispatch a WhatsApp or email reminder in bulk without leaving the screen.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Flexible fee head configuration with instalment schedules",
          "Scholarship and concession management with approval workflows",
          "Online payment gateway integration for student self-service",
          "Automatic late fee calculation beyond due dates",
          "Real-time collection dashboard with target vs. actual tracking",
          "Detailed receipts with digital signature and QR code verification",
          "Bulk invoice generation for entire batches in one click",
          "Outstanding dues report exportable to Excel for bank submission",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "Always reconcile the daily collection report at end of day before closing the cashier session. Unclosed sessions prevent the next day's payments from being allocated correctly to the correct accounting period.",
      },
    ],
  },

  // 3 — Attendance
  {
    slug: "attendance",
    section: "modules",
    title: "Attendance",
    subtitle: "Automated attendance capture, reports, and shortage alerts",
    icon: Clock,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Attendance module provides faculty with a fast, mobile-friendly interface to mark attendance subject-wise. Students and parents can view attendance in real time, and the system flags shortage warnings automatically.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student", "parent"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Attendance is one of the most operationally intensive tasks in an institution — recorded multiple times a day, for every subject, across every batch. Acadence streamlines this by letting faculty mark attendance from any device in under 60 seconds per class. The system automatically calculates cumulative attendance percentages, triggers shortage alerts when a student falls below the configured threshold (typically 75%), and notifies parents via WhatsApp.",
      },
      {
        type: "mockup",
        id: "attendance-calendar",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Mark Attendance",
      },
      {
        type: "steps",
        items: [
          {
            title: "Select today's class",
            body: "Navigate to Attendance > Mark Attendance. The system auto-populates today's scheduled classes based on the timetable. Click on the relevant subject-batch combination to open the attendance sheet.",
          },
          {
            title: "Review the student list",
            body: "All enrolled students appear with their roll numbers and photos. By default, everyone is marked Present. Toggle individual students to Absent or Late using the quick-tap buttons.",
          },
          {
            title: "Add remarks (optional)",
            body: "For students on medical leave or approved field trips, click the note icon next to their name and enter a remark. This prevents the system from counting the absence toward the shortage threshold.",
          },
          {
            title: "Submit attendance",
            body: "Click 'Submit'. The system locks the record and updates each student's cumulative percentage. Students and parents receive an instant notification if an absence has been recorded.",
          },
          {
            title: "Review shortage alerts",
            body: "Go to Attendance > Shortage Report. Students below threshold are highlighted in red. Faculty can generate the shortage list and share it with the academic department for counselling sessions.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Subject-wise attendance tracking linked to the live timetable",
          "One-click bulk 'Mark All Present' with selective absentee toggling",
          "Configurable attendance threshold per programme (e.g., 75%, 80%)",
          "Automatic parent notification via WhatsApp and email on absence",
          "Medical leave and on-duty excusing with remark support",
          "Attendance calendar view for students showing day-wise history",
          "Monthly and semester attendance reports exportable as PDF",
          "Integration with examination eligibility — ineligible students are flagged automatically",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Enable the 'Proxy Alert' setting under Attendance > Settings. When the same student is marked present in two different classrooms at the same time slot, the system raises a flag for the admin to review.",
      },
    ],
  },

  // 4 — LMS
  {
    slug: "lms",
    section: "modules",
    title: "LMS",
    subtitle: "Create and deliver courses, assignments, and quizzes online",
    icon: BookOpen,
    readTime: "7 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Learning Management System (LMS) module allows faculty to build structured online courses with video lectures, reading materials, quizzes, and assignments. Students access all content through a clean course dashboard.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Modern education extends well beyond the classroom. The LMS module enables faculty to create self-paced or scheduled online courses organised into units and topics. Each topic can contain video lectures (uploaded or YouTube/Vimeo embeds), PDF notes, external links, quizzes with automatic grading, and assignment submissions with deadline management. Students track their progress; faculty monitor engagement and completion rates.",
      },
      {
        type: "mockup",
        id: "lms-courses",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Create a Course",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create a new course",
            body: "Go to LMS > Courses > New Course. Enter the course title, description, programme, semester, and the faculty member(s) assigned as instructors. Set the course start and end dates and choose between 'Self-Paced' and 'Scheduled' delivery modes.",
          },
          {
            title: "Build the curriculum",
            body: "Inside the course builder, add Units (e.g., 'Unit 1: Introduction to Data Structures'). Within each unit, add Topics. Each topic can hold multiple content items — drag and drop to reorder them.",
          },
          {
            title: "Upload content",
            body: "Inside a topic, click 'Add Content'. Choose from Video (upload MP4 or paste a YouTube link), Document (PDF, DOCX), Image, or External Link. Add a title and optional description for each item. The system automatically stores videos on the CDN and generates thumbnails.",
          },
          {
            title: "Create a quiz or assignment",
            body: "Click 'Add Quiz' within a topic to open the quiz builder. Add multiple-choice, true/false, or short-answer questions. Set the total marks, pass mark, and attempt limit. For assignments, set a submission deadline and upload a rubric file for students to reference.",
          },
          {
            title: "Publish the course",
            body: "When the curriculum is complete, click 'Publish'. Enrolled students are notified and can begin accessing content. You can schedule publication for a future date to control course availability.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Drag-and-drop course builder with units and topics hierarchy",
          "Video hosting with streaming, YouTube/Vimeo embed support",
          "Auto-graded quizzes with multiple question types and instant feedback",
          "Assignment submission portal with deadline countdown and late submission flags",
          "Student progress tracking with per-topic completion percentages",
          "Discussion forum per course for peer and faculty interaction",
          "Course analytics: enrolment, engagement, quiz score distributions",
          "Downloadable course completion certificates upon finishing all modules",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Clone Course' feature to copy a course from a previous semester. This copies the entire curriculum structure so you only need to update dates and swap out outdated content rather than rebuilding from scratch.",
      },
    ],
  },

  // 5 — Examinations
  {
    slug: "examinations",
    section: "modules",
    title: "Examinations",
    subtitle: "Schedule exams, manage hall tickets, and oversee invigilation",
    icon: ClipboardList,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Examinations module covers everything from creating an exam schedule and assigning invigilation duties to generating hall tickets and managing the examination centre setup. It integrates with Attendance for eligibility checks.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Running examinations at an institutional scale requires careful coordination of rooms, invigilators, and student seating. The Examinations module lets the exam cell create exam sessions (internal, mid-semester, end-semester, supplementary), map subjects to dates and times, assign faculty invigilators, and generate hall tickets automatically for eligible students. It also flags students who are ineligible due to attendance or fee defaults.",
      },
      {
        type: "mockup",
        id: "exams-schedule",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Set Up an Exam",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create an exam session",
            body: "Navigate to Examinations > Sessions > New Session. Provide a session name (e.g., 'End Semester Exam — May 2026'), select the academic year and semester, and set the session start and end dates.",
          },
          {
            title: "Schedule subjects",
            body: "Within the session, click 'Add Exam'. Select the subject, date, start time, duration, and exam type (Written, Practical, Viva). Repeat for all subjects in the programme. The system checks for scheduling conflicts automatically.",
          },
          {
            title: "Assign rooms and invigilators",
            body: "Go to the Room Assignment tab. Select available rooms and set their capacity. The system suggests a seating arrangement based on enrolled student count. Assign invigilators from the faculty list and set the required invigilation ratio.",
          },
          {
            title: "Check eligibility",
            body: "Run the Eligibility Check from the session dashboard. The system cross-references attendance percentages and fee clearance. Students who fail any criterion are listed as ineligible with the specific reason, allowing the admin to communicate and resolve cases before hall tickets are released.",
          },
          {
            title: "Generate and release hall tickets",
            body: "Once eligibility is confirmed, click 'Generate Hall Tickets'. The system creates hall tickets for all eligible students with their seat number, room number, exam schedule, and institution stamp. Students are notified and can download their hall ticket from the student portal.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Multi-session support: internal, mid-term, end-semester, supplementary",
          "Automated conflict detection for date, room, and faculty scheduling",
          "Eligibility gate based on attendance threshold and fee clearance",
          "Auto-generated hall tickets with QR code verification",
          "Seating plan generator with room-wise student allocation",
          "Invigilation duty roster with downloadable faculty schedules",
          "Answer sheet tracking with barcode-based bundle management",
          "Supplementary exam scheduling directly from the failed results list",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "Hall tickets once released to students cannot be regenerated with different seat numbers unless the session is explicitly reset. Ensure room assignments and seating plans are finalised before releasing hall tickets.",
      },
    ],
  },

  // 6 — Results & Evaluation
  {
    slug: "results",
    section: "modules",
    title: "Results & Evaluation",
    subtitle: "Enter marks, compute grades, and publish student results",
    icon: Award,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Results module gives faculty a structured marks entry interface and the admin a publishing workflow. It computes grades using configurable grading schemes, generates mark sheets, and integrates with the parent portal.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student", "parent"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "After examinations are conducted, marks entry and result publication are time-sensitive tasks. The Results module separates these into two distinct steps: faculty enter marks in their respective subjects, and the admin reviews, approves, and publishes. The system handles grade computation automatically based on the configured grading scale (letter grades, GPA, percentage, or custom), identifies fails, and generates tabulated mark sheets.",
      },
      {
        type: "mockup",
        id: "results-card",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Publish Results",
      },
      {
        type: "steps",
        items: [
          {
            title: "Open the exam session",
            body: "Go to Results > Sessions and select the relevant exam session (e.g., 'End Semester Exam — May 2026'). The dashboard shows completion status for marks entry across all subjects.",
          },
          {
            title: "Enter marks",
            body: "Faculty navigate to Results > Enter Marks, select their subject, and see the enrolled students list. Enter the internal marks, external marks, and practical marks (where applicable). The system validates marks against the maximum configured for each component.",
          },
          {
            title: "Review and approve",
            body: "The admin opens the Approval Queue. Subjects with completed marks entry appear here. Review the distribution (the system shows pass/fail ratios and grade distribution charts) and approve each subject. Flagged anomalies (e.g., all students scoring identically) are highlighted.",
          },
          {
            title: "Compute grades",
            body: "Click 'Compute Grades' on the session dashboard. The system applies the grading scheme, calculates SGPA/CGPA, identifies backlogs, and prepares the results data.",
          },
          {
            title: "Publish to portal",
            body: "Click 'Publish'. Students and parents are immediately notified via the portal and WhatsApp. Students can view their mark sheet; parents can see the grade summary. Confidential mark breakdown remains visible only to students and faculty.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Component-wise marks entry: internal, external, practical, project",
          "Configurable grading schemes with letter grades, GPA, or custom scales",
          "SGPA and CGPA calculation across semesters with backlog tracking",
          "Faculty marks entry with admin approval gate before publication",
          "Grade distribution charts for academic analysis",
          "Printable mark sheets and consolidated result tabulation sheets",
          "Automatic identification of students with backlogs and supplementary exam eligibility",
          "Result withholding for fee defaulters with one-click release upon clearance",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Grace Marks' feature under the approval step to apply university-mandated grace marks globally across a subject. The system recalculates all grades instantly without requiring faculty to re-enter marks.",
      },
    ],
  },

  // 7 — Academic Planning
  {
    slug: "academic-planning",
    section: "modules",
    title: "Academic Planning",
    subtitle: "Build timetables, manage academic calendars, and plan lessons",
    icon: Calendar,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Academic Planning module lets coordinators build conflict-free timetables, publish the academic calendar, and help faculty plan lesson delivery across the semester. Timetables feed directly into Attendance and Examinations.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Creating a timetable for dozens of courses, faculty, and classrooms without conflicts is one of the most complex administrative tasks in academia. Acadence's Academic Planning module provides a visual drag-and-drop timetable builder that automatically detects and prevents scheduling conflicts. Once published, the timetable drives attendance recording and helps faculty create week-by-week lesson plans with aligned learning outcomes.",
      },
      {
        type: "mockup",
        id: "timetable-grid",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Create a Timetable",
      },
      {
        type: "steps",
        items: [
          {
            title: "Set up working hours",
            body: "Go to Academic Planning > Settings. Define the institution's daily working periods (e.g., 8 periods of 50 minutes each), break times, and working days. This configuration is used as the timetable grid template.",
          },
          {
            title: "Create a new timetable",
            body: "Navigate to Academic Planning > Timetables > New Timetable. Select the programme, semester, batch, and academic year. The system opens the weekly timetable grid with rows for days and columns for periods.",
          },
          {
            title: "Assign subjects to slots",
            body: "Drag a subject card from the subject bank on the right and drop it into any empty grid cell. Assign the faculty member and classroom for each slot. The system highlights any conflicts in real time — same faculty in two rooms, same classroom double-booked, or student batch scheduled in two places.",
          },
          {
            title: "Resolve conflicts and finalise",
            body: "The conflict panel lists all clashes with suggested alternatives. Swap slots by dragging until the timetable is clean. Use 'Auto-Suggest' to let the system generate a conflict-free draft that you can then fine-tune manually.",
          },
          {
            title: "Publish the timetable",
            body: "Click 'Publish'. All faculty and students in the batch receive a notification and can view their personalised timetable in the portal. The published timetable automatically populates the Attendance module's class list.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Visual drag-and-drop grid timetable builder with real-time conflict detection",
          "Faculty, classroom, and batch conflict checking in a single validation step",
          "Auto-suggest algorithm for generating conflict-free draft timetables",
          "Personalised timetable views for faculty, students, and room/lab schedulers",
          "Academic calendar with holidays, events, and exam periods",
          "Lesson plan builder aligned to syllabus units and course outcomes",
          "Mid-semester timetable revision with change notifications",
          "Export timetable to PDF, iCal, or Excel for offline use",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Mark public holidays and institutional events in the Academic Calendar before building timetables. The timetable builder automatically skips those dates when scheduling, preventing manual clean-up later.",
      },
    ],
  },

  // 8 — HR Management
  {
    slug: "hr",
    section: "modules",
    title: "HR Management",
    subtitle: "Staff records, onboarding, leave management, and appraisals",
    icon: Users,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The HR Management module is the central repository for all employee data. It handles the full employee lifecycle: recruitment, onboarding, service records, leave management, transfers, and appraisals.",
      },
      {
        type: "role-badge",
        roles: ["admin", "hr_manager"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "An educational institution's most important asset is its people — faculty, administrative staff, and support personnel. The HR Management module gives HR managers a single platform to maintain accurate service books, manage leave balances, process appraisals, and ensure statutory compliance. It integrates with Payroll to ensure salary calculations always reflect current employment terms.",
      },
      {
        type: "mockup",
        id: "hr-directory",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Onboard a New Employee",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create the employee record",
            body: "Go to HR > Employees > Add Employee. Fill in personal details (name, date of birth, gender, contact), employment details (joining date, department, designation, employment type), and bank account information for salary disbursement.",
          },
          {
            title: "Upload documents",
            body: "In the Documents tab, upload the employee's appointment letter, educational certificates, ID proofs, and experience certificates. Each document type has a predefined checklist — incomplete documents are flagged until the HR confirms receipt.",
          },
          {
            title: "Configure salary structure",
            body: "In the Salary tab, assign a salary template (created under Payroll) or manually enter the CTC breakdown: basic, HRA, conveyance, special allowances, and deductions. This links the employee to the payroll run.",
          },
          {
            title: "Set up leave entitlements",
            body: "Under the Leave tab, assign the applicable leave policy (Casual Leave, Earned Leave, Medical Leave, etc.) based on the employee's category. Opening balances are set automatically from the policy; manual adjustments are logged.",
          },
          {
            title: "Activate the account",
            body: "Click 'Activate'. The system creates a portal login, sends the employee a welcome email with credentials, and adds them to the relevant department roster. The employee now appears in timetable assignments, payroll, and attendance.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Comprehensive digital service book with full employment history",
          "Document vault with checklist-based verification for each employee",
          "Leave management: application, approval workflow, balance tracking",
          "Appraisal module with self-assessment and manager scoring",
          "Department and designation hierarchy management",
          "Transfer and promotion tracking with historical records",
          "Statutory compliance: PF, ESI, TDS configuration per employee",
          "Headcount and attrition analytics dashboards",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Bulk Import' feature to onboard multiple employees at once using the provided Excel template. This is particularly useful during the start of the academic year when new faculty are joining simultaneously.",
      },
    ],
  },

  // 9 — Payroll
  {
    slug: "payroll",
    section: "modules",
    title: "Payroll",
    subtitle: "Automate salary computation, payslips, and statutory compliance",
    icon: DollarSign,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Payroll module automates monthly salary processing for all employees. It computes earnings and deductions, applies leave and attendance adjustments, handles PF/ESI/TDS, and generates payslips — ready to disburse in minutes.",
      },
      {
        type: "role-badge",
        roles: ["admin", "hr_manager"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Monthly payroll is a high-stakes, deadline-driven process. The Payroll module pulls salary structures from HR records and leave/attendance data to compute accurate net pay for every employee. Statutory deductions (PF, ESI, Professional Tax, TDS) are calculated automatically based on configured rules. The system generates bank transfer files for direct salary disbursement and detailed payslips that employees can access through their portal.",
      },
      {
        type: "mockup",
        id: "payroll-slip",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Process Monthly Payroll",
      },
      {
        type: "steps",
        items: [
          {
            title: "Initiate the payroll run",
            body: "Navigate to Payroll > Process Payroll > New Run. Select the month and year. The system loads all active employees and their current salary structures. It also fetches leave deductions and loss-of-pay (LOP) days from the Attendance module.",
          },
          {
            title: "Review adjustments",
            body: "The Adjustments panel allows you to add one-time payments (bonus, arrears) or deductions (advance recovery, fines) for specific employees. These are applied on top of the standard salary computation.",
          },
          {
            title: "Run computation",
            body: "Click 'Compute'. The system calculates gross pay, applies all deductions, and computes net pay for each employee. A summary table shows total payroll cost, total deductions, and net disbursement. Review anomalies (employees with unusually high or low pay) flagged in the exceptions list.",
          },
          {
            title: "Approve the payroll run",
            body: "Once satisfied, the authorised signatory (admin or finance head) clicks 'Approve'. This locks the payroll run — no further changes are possible without reversing it. This step creates an audit trail.",
          },
          {
            title: "Disburse and send payslips",
            body: "Download the bank transfer file (NEFT/RTGS format) for your bank. Click 'Send Payslips' to email each employee a password-protected PDF payslip. Employees can also view and download payslips from the HR portal at any time.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Template-based salary structures with configurable earnings and deductions",
          "Automatic LOP deduction linked to attendance records",
          "PF, ESI, Professional Tax, and TDS computation per statutory slabs",
          "One-time adjustments for bonuses, arrears, and advance recovery",
          "Multi-level approval workflow with audit trail",
          "Bank transfer file generation in NEFT/RTGS format",
          "Password-protected payslip PDFs with digital dispatch",
          "Year-end Form 16 generation for TDS compliance",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "Once a payroll run is approved and disbursed, it cannot be edited. If a correction is required, use the 'Supplementary Run' feature to process differential payments in the following month, maintaining a clean audit trail.",
      },
    ],
  },

  // 10 — Placement Management
  {
    slug: "placement",
    section: "modules",
    title: "Placement Management",
    subtitle: "Manage campus drives, offers, and placement statistics",
    icon: Briefcase,
    readTime: "6 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Placement Management module gives the Training & Placement cell a full toolkit to coordinate campus recruitment drives, track student registrations and selection rounds, record offers, and publish placement statistics.",
      },
      {
        type: "role-badge",
        roles: ["admin", "placement_officer", "student"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Campus placement season involves managing multiple companies, dozens of selection rounds, and hundreds of students simultaneously. The Placement module lets placement officers create drives, set eligibility criteria (CGPA cutoff, backlogs, academic year), and manage round-by-round shortlisting. Students register through the portal, upload resumes, and receive status notifications. The T&P cell tracks offers, packages, and publishes placement reports for accreditation.",
      },
      {
        type: "mockup",
        id: "placement-stats",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Run a Placement Drive",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create a company drive",
            body: "Go to Placement > Drives > New Drive. Enter the company name, job role, location, package offered (CTC), drive date, and eligible programmes/batches. Add the selection process rounds (Aptitude Test, Group Discussion, Technical Interview, HR Interview).",
          },
          {
            title: "Set eligibility criteria",
            body: "Under Eligibility, configure minimum CGPA, maximum number of active backlogs, and graduation year. The system automatically generates an eligible student list from the enrolled database. Students who don't meet the criteria are excluded from registration.",
          },
          {
            title: "Open student registrations",
            body: "Click 'Open Registration'. Eligible students receive a notification in the portal and can register for the drive. Students upload their updated resume and fill in a placement profile if not already completed.",
          },
          {
            title: "Manage selection rounds",
            body: "After each selection round, the placement officer uploads or manually enters the shortlisted student list for the next round. The system tracks each student's current status (Registered, Shortlisted Round 1, Shortlisted Round 2, Selected, Not Selected) and notifies them at each stage.",
          },
          {
            title: "Record offers and close drive",
            body: "For each selected student, enter the offered package and offer letter date. Click 'Close Drive'. The system marks selected students as 'Placed' and publishes updated placement statistics on the dashboard.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Company drive management with structured selection round tracking",
          "CGPA and backlog-based automatic eligibility filtering",
          "Student placement profile with resume upload and skill tagging",
          "Round-by-round shortlisting with instant student notifications",
          "Offer letter upload and package recording per student",
          "Placement statistics dashboard: offers, packages, company-wise breakdown",
          "Batch-wise placement report for NIRF and accreditation submissions",
          "Alumni placement tracking for longitudinal career outcome data",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Pre-Placement Talk' feature to schedule an online or offline slot for companies to brief students before the drive. This reduces no-shows and gives companies more engaged candidates, improving offer conversion rates.",
      },
    ],
  },

  // 11 — OBE / NAAC
  {
    slug: "obe-naac",
    section: "modules",
    title: "OBE / NAAC",
    subtitle: "Outcome-based education mapping, CO/PO attainment, and NAAC reports",
    icon: BarChart2,
    readTime: "7 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The OBE / NAAC module supports Outcome-Based Education by mapping Course Outcomes (COs) and Programme Outcomes (POs), computing attainment levels, and generating the reports required for NAAC accreditation and NBA compliance.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Regulatory bodies such as NAAC and NBA require institutions to demonstrate that their programmes systematically achieve defined learning outcomes. The OBE module enables faculty to define Course Outcomes per subject, map them to Programme Outcomes and Programme-Specific Outcomes, attach them to assessment components (exam questions, assignments, projects), and automatically compute attainment after results are entered. This data flows directly into NAAC criterion-wise reports.",
      },
      {
        type: "mockup",
        id: "obe-report",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Set Up OBE",
      },
      {
        type: "steps",
        items: [
          {
            title: "Define Programme Outcomes",
            body: "Go to OBE > Programme Outcomes. For each programme (e.g., B.Tech Computer Science), define the 12 standard POs as specified by NBA, or customise them. These are the broad graduate attributes the programme aims to achieve.",
          },
          {
            title: "Create Course Outcomes",
            body: "Navigate to OBE > Course Outcomes and select a subject. Define 4–6 Course Outcomes (COs) describing what a student should be able to do after completing the course. Write them using Bloom's Taxonomy action verbs (analyse, evaluate, design, etc.).",
          },
          {
            title: "Map COs to POs",
            body: "Use the CO-PO mapping matrix to indicate the strength of relationship (High: 3, Medium: 2, Low: 1, None: 0) between each CO and each PO. This matrix is used to compute PO attainment from CO attainment.",
          },
          {
            title: "Map assessments to COs",
            body: "In the Results module's mark entry screen, each question or assignment component can be tagged with the COs it assesses. The system uses this tagging to compute CO-wise attainment from student performance data.",
          },
          {
            title: "Compute and analyse attainment",
            body: "After results are published, go to OBE > Attainment. Run the computation for the selected programme and semester. The system shows CO attainment per course, PO attainment, and a gap analysis highlighting outcomes below the target attainment threshold.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "CO and PO definition with Bloom's Taxonomy level tagging",
          "CO-PO mapping matrix with configurable relationship strength",
          "Assessment-to-CO tagging at question and component level",
          "Direct and indirect attainment calculation (exam + survey data)",
          "Gap analysis with programme-wide attainment heat map",
          "NAAC criterion-wise data compilation and export",
          "NBA SAR (Self-Assessment Report) data preparation support",
          "Year-on-year attainment comparison for continuous improvement tracking",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "OBE attainment data is only as accurate as the CO-to-assessment mapping. Ensure all faculty map their examination questions and assignments to COs at the start of each semester — retroactive mapping after results are entered is possible but time-consuming.",
      },
    ],
  },

  // 12 — Certificates
  {
    slug: "certificates",
    section: "modules",
    title: "Certificates",
    subtitle: "Generate, verify, and issue bonafide, transfer, and other certificates",
    icon: FileText,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Certificates module allows students to request certificates online and administrators to generate, approve, and issue them. Each certificate carries a unique QR code for instant third-party verification.",
      },
      {
        type: "role-badge",
        roles: ["admin", "student"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Students frequently need official documents — bonafide certificates for bank accounts or internship applications, transfer certificates, migration certificates, provisional degree certificates, and character certificates. The Certificates module digitises this process: students raise requests through the portal, administrators review and approve them, and the system generates institution-branded PDFs with a QR code that anyone can scan to verify authenticity online.",
      },
      {
        type: "mockup",
        id: "certificate-preview",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Generate a Certificate",
      },
      {
        type: "steps",
        items: [
          {
            title: "Student raises a request",
            body: "The student logs in to the portal, navigates to Certificates > Request Certificate, and selects the certificate type. They provide a purpose (e.g., 'For bank account opening') and any additional details the certificate type requires. They can also track the status of previous requests.",
          },
          {
            title: "Admin reviews the request",
            body: "The admin opens Certificates > Pending Requests. Each request shows the student details, certificate type, purpose, and current student status (fee clearance, active enrollment). The admin can approve, return for clarification, or reject.",
          },
          {
            title: "System generates the certificate",
            body: "On approval, the system merges the student's data into the pre-designed certificate template. The generated PDF includes the institution name, logo, principal's signature (digital), and a unique certificate number.",
          },
          {
            title: "QR code and dispatch",
            body: "A QR code linking to the online verification page is embedded in the certificate. The system emails the PDF to the student and marks the request as completed. Physical printing can be done by the admin from the print queue.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Multiple certificate types: bonafide, TC, migration, character, provisional degree",
          "Student self-service request portal with real-time status tracking",
          "Custom certificate template designer with drag-and-drop field placement",
          "QR-code-based online verification without requiring portal login",
          "Unique serial number generation with tamper-evident design",
          "Configurable approval workflow (single or multi-level sign-off)",
          "Bulk certificate generation for graduating batch in one operation",
          "Certificate issuance register maintained automatically for institutional records",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the template editor to create version-specific templates with the current principal's digital signature. When leadership changes, update the template once — all future certificates will use the new signature without retroactively modifying existing issued certificates.",
      },
    ],
  },

  // 13 — Feedback Forms
  {
    slug: "feedback",
    section: "modules",
    title: "Feedback Forms",
    subtitle: "Collect, analyse, and act on structured feedback from all stakeholders",
    icon: MessageSquare,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Feedback module provides a configurable form builder for collecting feedback from students, faculty, and parents. Responses are aggregated into analytics dashboards to inform academic decisions and satisfy accreditation requirements.",
      },
      {
        type: "role-badge",
        roles: ["admin", "student", "faculty"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Systematic feedback collection is a cornerstone of NAAC accreditation (Criterion 2) and of institutional quality improvement. The Feedback module lets administrators design forms with various question types, target them to specific respondent groups (e.g., final-year students for exit surveys, parents for parent-teacher meetings), set collection windows, and view response analytics. Faculty can view anonymised feedback about their own courses to improve teaching.",
      },
      {
        type: "mockup",
        id: "feedback-form",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Create a Feedback Form",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create a new form",
            body: "Navigate to Feedback > Forms > New Form. Give the form a name (e.g., 'Course Feedback — Odd Semester 2026'), select the respondent type (student, faculty, or parent), and set the collection window (start and end dates).",
          },
          {
            title: "Design questions",
            body: "Use the drag-and-drop form builder to add questions. Question types include Rating Scale (1–5 or 1–10), Multiple Choice, Checkbox, Short Text, Long Text, and Net Promoter Score. Organise questions into sections (e.g., 'Curriculum', 'Teaching Quality', 'Infrastructure').",
          },
          {
            title: "Assign to respondents",
            body: "Under Assignment, select the target groups: programmes, batches, or individual students/faculty. For course feedback, enable 'Subject-wise' mode so the same form is sent once per enrolled subject, and responses are automatically categorised.",
          },
          {
            title: "Launch and monitor",
            body: "Click 'Launch'. Respondents receive a notification in the portal and, if WhatsApp integration is active, a direct link. Monitor the response rate dashboard in real time. Send automated reminders to non-respondents close to the deadline.",
          },
          {
            title: "Analyse results",
            body: "After the collection window closes, view the analytics dashboard. See average scores per question, section-wise summaries, and verbatim responses. Export a faculty-wise comparison report or a full CSV of responses for deeper analysis.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Drag-and-drop form builder with six question types",
          "Subject-wise course feedback with automatic faculty-result linkage",
          "Configurable anonymity settings to ensure honest responses",
          "Real-time response rate monitoring with automated reminders",
          "Analytics dashboard with averages, distributions, and verbatim responses",
          "Faculty-visible anonymised course feedback for self-improvement",
          "NAAC-aligned feedback categories for direct criterion mapping",
          "Export to Excel or PDF for accreditation document preparation",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Enable 'Anonymous Responses' for faculty feedback forms. Anonymity significantly increases response rates and candour, giving faculty more actionable insights than identified surveys typically yield.",
      },
    ],
  },

  // 14 — Document Management
  {
    slug: "documents",
    section: "modules",
    title: "Document Management",
    subtitle: "Centralised institutional document repository with version control",
    icon: FolderOpen,
    readTime: "4 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Document Management module provides a structured, access-controlled repository for institutional documents — policies, circulars, academic records, HR files, and accreditation documents — with version history and audit trails.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student", "hr_manager"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Educational institutions deal with a vast volume of official documents: government circulars, university affiliation letters, examination board communications, NAAC/NBA documents, HR policies, and student records. The Document Management module replaces disorganised shared drives with a structured, searchable repository. Documents are categorised, version-controlled, and access-controlled — ensuring the right people see the right documents and old versions are always retrievable.",
      },
      {
        type: "mockup",
        id: "document-manager",
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Hierarchical folder structure with category tagging (HR, Academic, Finance, Accreditation)",
          "Version control: each upload creates a new version while preserving the history",
          "Role-based access control — granular permissions at folder or document level",
          "Full-text search across document titles, tags, and metadata",
          "Document expiry reminders for licences, affiliations, and compliance certificates",
          "Activity audit log showing who accessed, downloaded, or modified each document",
          "Bulk upload with automatic metadata extraction from filenames",
          "Integration with Certificates and HR modules for automatic document filing",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Create a dedicated 'NAAC Documents' folder hierarchy matching the seven NAAC criteria. When the institution prepares for re-accreditation, all relevant documents are instantly findable rather than scattered across departments.",
      },
    ],
  },

  // 15 — Inward / Outward
  {
    slug: "inward-outward",
    section: "modules",
    title: "Inward / Outward",
    subtitle: "Track all official correspondence entering and leaving the institution",
    icon: Mail,
    readTime: "4 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Inward / Outward module provides a digital dispatch register for all official correspondence. Every letter, parcel, or communication received or sent is logged, numbered, and tracked through to acknowledgement.",
      },
      {
        type: "role-badge",
        roles: ["admin", "hr_manager"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Government-affiliated institutions are required to maintain formal inward and outward dispatch registers. The module digitises these registers, replacing manual entry with a searchable, auditable electronic log. Every inbound letter — from a university, government body, vendor, or individual — is assigned an inward number and routed to the relevant department. Outgoing letters are assigned an outward number, scanned copies are stored, and dispatch confirmations are logged.",
      },
      {
        type: "mockup",
        id: "register-table",
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Sequential inward and outward register numbers with date-stamping",
          "Sender/recipient details with category tagging (University, Government, Vendor, Internal)",
          "Scanned copy attachment for every registered communication",
          "Internal routing to departments or individuals with acknowledgement tracking",
          "Reply linkage — outward entries can reference the inward letter they respond to",
          "Overdue follow-up alerts for pending responses",
          "Date-range search and export for audit or compliance purposes",
          "Role-based access: front-desk staff record, managers view department-level correspondence",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Enable the 'Auto-Number on Save' setting so dispatch numbers are assigned only when the entry is saved, preventing number gaps caused by abandoned drafts — keeping your register clean for inspection.",
      },
    ],
  },

  // 16 — Mentor-Mentee
  {
    slug: "mentor-mentee",
    section: "modules",
    title: "Mentor-Mentee",
    subtitle: "Structured mentoring with session logs, goals, and progress tracking",
    icon: Heart,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Mentor-Mentee module formalises the mentoring relationship between faculty and students. It manages pairings, session scheduling, goal tracking, and progress notes — providing verifiable evidence for NAAC Criterion 5.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Structured mentoring is both an NAAC requirement and a genuine student-success driver. The Mentor-Mentee module enables systematic pairing (either manually or algorithmically based on department and batch), periodic session scheduling, and session note logging. Students can view their mentor's notes and set goals collaboratively. Admins can see mentoring coverage and flag students with no recent sessions, ensuring no student falls through the cracks.",
      },
      {
        type: "mockup",
        id: "mentor-pairing",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Set Up Mentoring",
      },
      {
        type: "steps",
        items: [
          {
            title: "Create a mentoring cohort",
            body: "Go to Mentor-Mentee > Cohorts > New Cohort. Select the academic year, programme, and batch. Define the mentoring period (e.g., Odd Semester 2026) and the expected session frequency (monthly, fortnightly).",
          },
          {
            title: "Assign mentor-mentee pairs",
            body: "Choose 'Auto Assign' to let the system distribute students evenly across available faculty mentors within the department, or use 'Manual Assign' to specify pairings. Typical ratio is 1 faculty mentor to 15–20 students.",
          },
          {
            title: "Schedule sessions",
            body: "Mentors view their assigned mentees in the portal under My Mentees. Click 'Schedule Session' to set a meeting date and time. Students receive a portal and WhatsApp notification. Sessions can be marked as individual or group meetings.",
          },
          {
            title: "Log session notes",
            body: "After each meeting, the faculty mentor logs notes in the session record: topics discussed, student concerns raised, action items agreed upon, and the mentor's assessment of the student's wellbeing and academic progress.",
          },
          {
            title: "Track goals and close the cohort",
            body: "Students and mentors collaboratively set and track goals (academic, career, personal) within the portal. At the end of the cohort period, the mentor submits a summary report. Admins can see coverage statistics and download mentoring records.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Automatic or manual mentor-mentee pairing with configurable student-to-mentor ratio",
          "Session scheduling with calendar integration and reminder notifications",
          "Structured session note templates covering academic, personal, and career dimensions",
          "Collaborative goal setting with status tracking across the mentoring period",
          "Admin dashboard with mentoring coverage: sessions held, students not met, overdue sessions",
          "Private notes visible only to the mentor and admin for sensitive disclosures",
          "End-of-period summary report generation for NAAC documentation",
          "Student feedback on mentoring effectiveness at cohort close",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Link the Attendance and Results data to the mentoring profile. This allows mentors to see attendance trends and grade trajectories at a glance before each session, making conversations more targeted and effective.",
      },
    ],
  },

  // 17 — Website Management
  {
    slug: "website",
    section: "modules",
    title: "Website Management",
    subtitle: "Manage your institution's public website content without coding",
    icon: Globe,
    readTime: "4 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Website Management module is a content management system (CMS) built into Acadence for managing your institution's public-facing website. Admins can update pages, publish news and events, and manage the faculty directory without developer involvement.",
      },
      {
        type: "role-badge",
        roles: ["admin"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "A well-maintained institutional website is critical for prospective student attraction, accreditation evidence, and stakeholder communications. The Website Management module decouples content management from technical development. Administrators use a visual editor to update programme information, publish news articles, add placement success stories, and manage event announcements — changes go live instantly without requiring web developer intervention.",
      },
      {
        type: "mockup",
        id: "cms-editor",
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Visual WYSIWYG page editor with rich text, images, and embedded media",
          "News and events management with scheduled publishing",
          "Faculty directory auto-synced from HR records — no duplicate data entry",
          "Placement statistics block auto-populated from Placement module data",
          "Gallery management with drag-and-drop image ordering",
          "Contact form and enquiry management routed to the admissions team",
          "SEO metadata editor for page titles, descriptions, and social preview images",
          "Version history for all page edits with one-click rollback",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "The Website Management module manages content only — it does not include domain or hosting management. Ensure your web infrastructure team has configured the CMS integration endpoint before expecting changes to reflect on the live site.",
      },
    ],
  },

  // 18 — Library
  {
    slug: "library",
    section: "modules",
    title: "Library",
    subtitle: "Catalogue, circulation, and digital resources for the institutional library",
    icon: Library,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Library module provides a full library management system: catalogue management, book issue and return, fine calculation, and access to subscribed digital resources — all integrated with the student and faculty portal.",
      },
      {
        type: "role-badge",
        roles: ["admin", "student", "faculty"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "An institutional library serves hundreds of users simultaneously. The Library module enables librarians to maintain a searchable catalogue, manage the physical circulation of books (issue, return, renew, reserve), calculate fines for overdue returns, and provide a student-facing OPAC (Online Public Access Catalogue). Faculty can recommend new acquisitions; students can check availability before visiting the library.",
      },
      {
        type: "mockup",
        id: "library-catalog",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Issue a Book",
      },
      {
        type: "steps",
        items: [
          {
            title: "Search the catalogue",
            body: "Go to Library > Catalogue. Search by title, author, ISBN, or subject keyword. The system shows all copies, their accession numbers, and current availability status (Available, Issued, Reserved).",
          },
          {
            title: "Identify the borrower",
            body: "Navigate to Library > Issue/Return. Scan or type the borrower's library card number or student/employee ID. The system shows the borrower's profile, current issues, and any overdue books or outstanding fines.",
          },
          {
            title: "Check pending fines",
            body: "If the borrower has outstanding fines, the system prompts for fine collection before issuing new books. Collect the fine amount, record the payment, and proceed.",
          },
          {
            title: "Issue the book",
            body: "Scan or enter the accession number of the book to be issued. The system records the issue date, due date (based on the configured loan period), and updates the catalogue availability. Print or email the borrower a due-date slip.",
          },
          {
            title: "Process return",
            body: "On return, scan the accession number under Library > Issue/Return > Return. The system calculates any overdue fine, updates the catalogue back to 'Available', and notifies the next person on the reservation queue if applicable.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "MARC-based catalogue with ISBN auto-fill from OpenLibrary API",
          "Barcode/QR-based issue and return for fast circulation desk operations",
          "Configurable loan periods and fine rates by borrower category",
          "OPAC for student and faculty book search and availability check",
          "Online reservation with queue management and notification",
          "Periodicals and journal tracking with subscription renewal reminders",
          "Digital resources section with links to subscribed e-databases and e-books",
          "Library usage reports: most-issued titles, active borrowers, peak usage periods",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Integrate the Library module with the LMS by tagging recommended readings per course. Students can click on a book reference in the LMS course page and see its real-time availability in the library catalogue.",
      },
    ],
  },

  // 19 — Visitor Management
  {
    slug: "visitor-management",
    section: "modules",
    title: "Visitor Management",
    subtitle: "Digital visitor check-in, gate passes, and campus access logs",
    icon: Shield,
    readTime: "4 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Visitor Management module replaces paper visitor registers with a digital check-in system. Every visitor is logged with photo capture, host notification, badge printing, and time-stamped entry and exit records.",
      },
      {
        type: "role-badge",
        roles: ["admin", "hr_manager"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Campus security depends on knowing who is on the premises at any given time. The Visitor Management module enables gate staff to check in visitors quickly using a tablet or desktop, capture their photo, log the person they are meeting, and print a visitor badge. Hosts (faculty or staff) receive an instant notification when their visitor arrives. At exit, the departure time is logged, providing a complete campus presence record.",
      },
      {
        type: "mockup",
        id: "visitor-checkin",
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Digital visitor registration with photo capture and ID document scan",
          "Pre-registration by hosts for expected visitors — faster entry on arrival",
          "Host notification via SMS, WhatsApp, or email when visitor arrives",
          "Visitor badge printing with name, photo, host, and time-limited QR code",
          "Entry and exit time logging with automatic gap detection for missing exits",
          "Blacklist management to prevent re-entry of flagged individuals",
          "Purpose and department-wise visitor analytics for security reviews",
          "Emergency evacuation report listing all visitors currently on campus",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Important Note",
        body: "Ensure gate staff are trained to use the 'Force Exit' function at end of day for visitors who may have left without checking out. Unresolved open entries skew campus presence reports and can cause false alarms during emergency headcounts.",
      },
    ],
  },

  // 20 — Communications
  {
    slug: "communications",
    section: "modules",
    title: "Communications",
    subtitle: "Reach all stakeholders instantly via WhatsApp, email, and SMS",
    icon: Bell,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Communications module provides a unified messaging centre for the institution. Send targeted or bulk messages via WhatsApp, email, or SMS to students, parents, faculty, and staff — with delivery tracking and template management.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "student", "parent", "hr_manager", "placement_officer"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Timely communication with the right stakeholders is critical to institutional operations — from notifying parents about fee due dates to alerting students about exam schedule changes. The Communications module integrates WhatsApp (via the Acadence WhatsApp service), email, and SMS into one composing interface. Messages can be sent to predefined groups (e.g., all third-year B.Tech students), individual users, or dynamically filtered segments.",
      },
      {
        type: "mockup",
        id: "whatsapp-composer",
      },
      {
        type: "heading",
        level: 2,
        text: "How to Send a Bulk Message",
      },
      {
        type: "steps",
        items: [
          {
            title: "Compose a new message",
            body: "Go to Communications > Compose. Select the channel: WhatsApp, Email, or SMS. Choose a pre-approved message template or write a new message. WhatsApp messages must use approved templates for broadcast; conversational replies are unrestricted.",
          },
          {
            title: "Select recipients",
            body: "In the Recipient panel, choose from: a pre-defined group (programme + batch), a role (all parents, all faculty), or a custom filter (e.g., students with attendance below 75%). The system shows the recipient count before sending.",
          },
          {
            title: "Personalise with merge fields",
            body: "Use merge fields like {{student_name}}, {{fee_amount}}, or {{due_date}} in your message body. The system substitutes the correct value for each recipient, making bulk messages feel personal.",
          },
          {
            title: "Schedule or send immediately",
            body: "Click 'Send Now' for immediate dispatch, or use 'Schedule' to set a future date and time. Scheduled messages appear in the queue and can be edited or cancelled before they are dispatched.",
          },
          {
            title: "Monitor delivery",
            body: "View the campaign report under Communications > Reports. For WhatsApp and SMS, delivery and read receipts are shown per recipient. Email reports show open and click rates. Failed deliveries are listed for retry.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Unified composer for WhatsApp, email, and SMS in one interface",
          "Template library with approved WhatsApp message templates",
          "Dynamic recipient filtering by programme, batch, role, and custom criteria",
          "Personalised merge fields per recipient for contextual messaging",
          "Scheduled messaging with campaign queue management",
          "Delivery and read receipt tracking per recipient per channel",
          "Automated trigger messages from other modules (attendance alert, fee reminder, result published)",
          "Opt-out management to comply with messaging regulations",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Set up automated trigger messages for high-frequency events — absence recorded, fee due in 3 days, result published. This removes the manual burden of sending routine notifications and ensures stakeholders are informed consistently.",
      },
    ],
  },

  // 21 — Reports & Analytics
  {
    slug: "reports",
    section: "modules",
    title: "Reports & Analytics",
    subtitle: "Institution-wide dashboards and exportable reports across all modules",
    icon: TrendingUp,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Module Overview",
        body: "The Reports & Analytics module aggregates data across all Acadence modules into a centralised dashboard. Administrators and department heads access pre-built reports and build custom ones for strategic decision-making and accreditation submissions.",
      },
      {
        type: "role-badge",
        roles: ["admin", "faculty", "hr_manager", "placement_officer"],
      },
      {
        type: "heading",
        level: 2,
        text: "Overview",
      },
      {
        type: "text",
        body: "Data silos prevent institutions from understanding their own performance. The Reports & Analytics module breaks down those silos by pulling from Admissions, Fees, Attendance, Results, Placement, HR, and all other modules into a single reporting layer. Pre-built reports cover the most common institutional needs; the custom report builder lets power users create bespoke cross-module analyses. All reports can be exported to PDF or Excel.",
      },
      {
        type: "mockup",
        id: "analytics-dashboard",
      },
      {
        type: "heading",
        level: 2,
        text: "Available Reports",
      },
      {
        type: "table",
        headers: ["Report", "Module", "Key Metrics"],
        rows: [
          ["Admissions Funnel", "Admissions", "Inquiries → Applications → Shortlisted → Enrolled conversion rates"],
          ["Fee Collection Summary", "Fees", "Collected vs. due, outstanding by programme, payment mode split"],
          ["Attendance Overview", "Attendance", "Average attendance %, shortage students count, subject-wise trends"],
          ["Examination Results", "Results", "Pass/fail rates, grade distribution, toppers list, backlog analysis"],
          ["Placement Statistics", "Placement", "Offers, average CTC, company-wise breakdown, batch-wise comparison"],
          ["HR Headcount", "HR", "Department-wise staff count, joining/attrition trends, leave utilisation"],
          ["Payroll Summary", "Payroll", "Total payroll cost, department-wise spend, month-on-month trends"],
          ["OBE Attainment", "OBE / NAAC", "CO and PO attainment levels, gap analysis, year-on-year improvement"],
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Features",
      },
      {
        type: "list",
        items: [
          "Executive dashboard with institution-wide KPIs on a single screen",
          "Pre-built report library covering all 20 functional modules",
          "Custom report builder with cross-module data joins",
          "Interactive charts with drill-down to individual records",
          "Scheduled report delivery via email to department heads",
          "Role-based report access — faculty see academic data; HR sees people data",
          "One-click export to PDF (formatted) or Excel (raw data) for every report",
          "NAAC and NIRF data compilation wizard for accreditation submissions",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Pro Tip",
        body: "Use the 'Scheduled Delivery' feature to email the Fee Collection Summary to the finance head and Principal every Monday morning. This replaces manual report generation and ensures leadership always has the latest data before weekly review meetings.",
      },
    ],
  },
];

export default modules;
